import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { Globals } from '../shared/globals';
import { QrCode } from '../models/qr-code.model';
import { QrCodeSource } from '../models/qr-code-source.model';
import { Injectable } from "@angular/core";
import { JsonSerializer } from 'typescript-json-serializer';
import { CustomStationCode } from "../models/custom-station-code";
import { Api } from "../shared/api";

/**
 * Service responsible for managing QR codes.
 *
 * @export
 * @class QrCodesService
 */
@Injectable()
export class QrCodesService {
  private jsonSerializer = new JsonSerializer();

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private globals: Globals,
    private api: Api
  ) {
  }

  public createQrCode(
    source: QrCodeSource,
    connectorId: number,
    customCode: string
  ): Promise<QrCode> {
    return this.authService.getToken().then((token) => {
      return this.performCreateQrCodeRequest(
        token,
        source,
        connectorId,
        customCode
      );
    });
  }

  public getQrCodeSources(): Promise<QrCodeSource[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetQrCodeSources(token);
    });
  }

  public getCustomStationCodesByStation(station: string): Promise<CustomStationCode[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetCustomStationCodesByStation(token, station);
    });
  }

  public async deleteCustomStationCode(identifier: string): Promise<void> {
    const url = `${this.globals.apiUrl('qr-codes')}/customcodes?code=${identifier}`;
    return await this.api.httpDelete(url);
  }

  // Internal methods

  private performCreateQrCodeRequest(
    token: string,
    source: QrCodeSource,
    connectorId: number,
    customCode: string
  ): Promise<QrCode> {
    const params = new URLSearchParams({
      connectorid: connectorId.toString(),
    });
    if (source) {
      params.append('stationid', source.stationId);
    }
    if (customCode) {
      params.append('customcode', customCode);
    }

    const url = new URL(this.globals.apiUrl('qr-codes') + '?' + params.toString());

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), null, { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<QrCode>(response, QrCode)
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
  * Get the list of stations for the owner
  */
  private performGetQrCodeSources(token: string): Promise<QrCodeSource[]> {
    const userId = this.authService.userId;
    const url = new URL(this.globals.apiUrl('users/' + userId + '/qr-codes/sources'));
    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<QrCodeSource>(response[item], QrCodeSource)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
  * Get the list of custom codes filtered by stationId
  */
  private performGetCustomStationCodesByStation(token: string, stationId: string): Promise<CustomStationCode[]> {
    const url = new URL(this.globals.apiUrl('qr-codes/customcodes') + '?stationId=' + stationId);
    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<CustomStationCode>(response[item], CustomStationCode)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

